import type { ReactNode } from "react";
import { useMemo } from "react";
import "./DailyTagsList.scss";
import { sortByKey } from "@/shared/utils/Arrays.ts";
import DailyTagCell from "@/pages/human-resources/time-management/components/daily-tags/DailyTagCell.tsx";
import classNames from "classnames";
import type { DailyTag } from "@/pages/human-resources/time-management/business/daily-tag/DailyTag.ts";
import CornerIconsLegend from "@/pages/human-resources/time-management/components/schedule-palette/bottom-row/legend/CornerIconsLegend.tsx";
import useKeywordsFilter from "@/shared/custom-hook/useKeywordsFilter.ts";
import HelpMessage from "@/pages/human-resources/time-management/components/daily-tags/HelpMessage.tsx";
import { findDailyTagsDuplicateIdsAndScopes } from "@/pages/human-resources/time-management/components/schedule-palette/bottom-row/legend/EntityScope.ts";
import { normalizedCharacters } from "@/shared/utils/Strings.ts";

interface Properties {
  dailyTags: DailyTag[];
  selectedDailyTag: DailyTag | undefined;
  onSelectDailyTag: (dt: DailyTag) => void;
  horizontal: boolean;
  searchInput: string;
  helpText?: string;
}

export default function DailyTagsList(props: Properties): ReactNode {
  const searchFilter = useKeywordsFilter(
    props.searchInput,
    props.dailyTags,
    (dt) => dt.label,
  );

  const duplicates = useMemo(() => {
    return findDailyTagsDuplicateIdsAndScopes(props.dailyTags);
  }, [props.dailyTags]);

  const sortedDailyTags = useMemo(() => {
    const res = [...props.dailyTags];
    sortByKey(res, (st) => normalizedCharacters(st.label));
    return res;
  }, [props.dailyTags]);

  return (
    <div
      className={classNames(
        "daily-tags-list",
        props.horizontal && "horizontal",
      )}
    >
      {props.helpText !== undefined && <HelpMessage text={props.helpText} />}
      <div className="scrollable-list">
        {sortedDailyTags.filter(searchFilter).map((t) => (
          <DailyTagCell
            key={t.id}
            dailyTag={t}
            isDuplicate={duplicates.entities.has(t)}
            selected={props.selectedDailyTag === t}
            onClick={() => {
              props.onSelectDailyTag(t);
            }}
          />
        ))}
      </div>
      <CornerIconsLegend scopes={duplicates.scopes} />
    </div>
  );
}
